import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Brand from "@/components/Brand";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not reset password");
    } finally { setBusy(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="max-w-md w-full text-center">
          <Brand size="md" />
          <p className="font-serif text-3xl mt-6">Invalid reset link.</p>
          <Link to="/login" className="mt-4 inline-block text-xs uppercase tracking-[0.2em] underline underline-offset-4">
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-background text-foreground">
      <div className="w-full max-w-md">
        <Brand size="md" />
        <p className="eyebrow mt-8">Stylebay · Password reset</p>
        <h1 className="editorial-h2 mt-3">Pick a new password.</h1>

        {done ? (
          <div className="mt-8 border border-border p-8 text-center" data-testid="reset-success">
            <CheckCircle2 size={32} className="mx-auto" />
            <p className="font-serif text-2xl mt-4">Password updated.</p>
            <p className="text-sm text-muted-foreground mt-2">Redirecting to sign-in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
            <div>
              <Label htmlFor="rp1" className="eyebrow">New password</Label>
              <Input id="rp1" type="password" required minLength={8}
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="rounded-none mt-2 h-11"
                data-testid="reset-password-input"
                placeholder="At least 8 characters" />
            </div>
            <div>
              <Label htmlFor="rp2" className="eyebrow">Confirm password</Label>
              <Input id="rp2" type="password" required minLength={8}
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                className="rounded-none mt-2 h-11"
                data-testid="reset-confirm-input"
                placeholder="Re-enter password" />
            </div>
            <Button type="submit" disabled={busy}
              className="w-full h-12 rounded-none uppercase tracking-[0.2em] text-xs"
              data-testid="reset-submit">
              {busy ? <><Loader2 size={14} className="animate-spin mr-2" /> Saving</> : "Update password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
